import React, { useMemo } from 'react';
import {
    FormControl,
    FormHelperText,
    FormLabel,
    makeStyles,
} from '@material-ui/core';
import type { FormField } from '../interfaces';
import { ActionButton, Block } from '@klp/design';
import { Add } from '@material-ui/icons';
import type { VedleggRequest } from '../services/rtkApi';
import {
    useGetProsjektProcessAttachmentsQuery,
    useSaveProsjektProcessAttachmentMutation,
    useDeleteProsjektProcessAttachmentMutation,
} from '../services/rtkApi';
import { Vedlegg } from './Vedlegg';
import { findCamundaPropertyValue, getLocalizedLabel } from '../utils';
import { FileUploadDynamic } from '@klp/file/components';
import type { FileObject } from '@klp/file/classes';
import { getFormFieldStyles } from '../formFieldStyles';
import { useField, useFormikContext } from 'formik';

const useStyles = makeStyles((theme) => ({
    vedleggListContainer: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),

        '& .MuiTableCell-root': {
            padding: theme.spacing(1),
        },
    },
}));

type VedleggSelectorProps = {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
    prosessInstansId: string;
    attachmentType: string | undefined;
    maxNumberOfAttachments?: number;
};

export const VedleggSelector = ({
    field,
    isReadOnly,
    isRequired,
    prosessInstansId,
    attachmentType,
    maxNumberOfAttachments,
}: VedleggSelectorProps) => {
    const classes = getFormFieldStyles();
    const localClasses = useStyles();
    const { setFieldValue } = useFormikContext();
    const [_, formikFieldMeta] = useField<string | null>(field.id);
    const { data: processAttachments } = useGetProsjektProcessAttachmentsQuery({
        prosessInstansId: prosessInstansId,
    });
    attachmentType =
        attachmentType ??
        findCamundaPropertyValue('attachmentType', field.properties);
    const maxAttachementsFromProperty = findCamundaPropertyValue(
        'maxNumberOfAttachments',
        field.properties,
    );
    maxNumberOfAttachments =
        maxNumberOfAttachments ??
        (maxAttachementsFromProperty
            ? parseInt(maxAttachementsFromProperty)
            : undefined);

    const attachments = useMemo(
        () =>
            processAttachments?.filter(
                (attachment) => attachment.vedleggType === attachmentType,
            ),
        [processAttachments, attachmentType],
    );

    const [saveAttachment, { isLoading: isSaving }] =
        useSaveProsjektProcessAttachmentMutation();
    const [deleteAttachment, { isLoading: isDeleting }] =
        useDeleteProsjektProcessAttachmentMutation();

    const handleDeleteVedlegg = (vedleggId: number) => {
        deleteAttachment({ attachmentId: vedleggId.toString() });
        const fieldValue =
            attachments && attachments.length > 1
                ? attachments
                      .filter((att) => att.id !== vedleggId)
                      .map((att) => att.name)
                      .join(', ')
                : null;
        setFieldValue(field.id, fieldValue);
    };

    const handleAddVedlegg = (file: FileObject) => {
        const attachment: VedleggRequest = {
            name: file.fileName,
            type: file.type,
            content: file.fileContent,
        };
        saveAttachment({
            prosessInstansId: prosessInstansId,
            vedleggType: attachmentType,
            attachment: attachment,
        })
            .unwrap()
            .then(() => {
                const fieldValue =
                    attachments && attachments.length > 0
                        ? attachments.map((att) => att.name).join(', ') +
                          ', ' +
                          file.fileName
                        : file.fileName;
                setFieldValue(field.id, fieldValue);
            });
    };

    const maxAttachmentAmountReached =
        attachments &&
        attachments.length >= (maxNumberOfAttachments ?? Infinity);

    return (
        <FormControl
            className={classes.editableField}
            error={Boolean(formikFieldMeta.error) && formikFieldMeta.touched}
            disabled={isReadOnly}
            required={isRequired}
            fullWidth={true}
        >
            <FormLabel component="legend">
                {getLocalizedLabel(
                    `project.formFields.${field.id}`,
                    field.label,
                    field.id,
                )}
            </FormLabel>

            <Block className={localClasses.vedleggListContainer}>
                {attachments?.map((attachment) => (
                    <Vedlegg
                        key={`${attachment.id}`}
                        vedlegg={attachment}
                        isReadOnly={isReadOnly}
                        onDeleteRow={() => handleDeleteVedlegg(attachment.id)}
                        isDeleting={isDeleting}
                    />
                ))}
            </Block>
            {!isReadOnly && (
                <FileUploadDynamic
                    mimeTypes="application/pdf"
                    queueName={`vedlegg-${attachmentType}`}
                    id={`vedlegg-${attachmentType}-uploader`}
                    fileUploadCallback={handleAddVedlegg}
                    disabled={isSaving || maxAttachmentAmountReached}
                >
                    <ActionButton
                        actionType="button"
                        translation="prosjekt.dokument.add"
                        isLoading={isSaving}
                        endIcon={<Add />}
                        disabled={maxAttachmentAmountReached}
                    />
                </FileUploadDynamic>
            )}

            {formikFieldMeta.touched && formikFieldMeta.error && (
                <FormHelperText
                    id="component-error-text"
                    className={classes.error}
                >
                    {formikFieldMeta.error}
                </FormHelperText>
            )}
        </FormControl>
    );
};
